/* ============================================================
 * frontend-v3/src/screens/channels/ChannelEdit.tsx
 *
 * Channel "Ajustes" — the post-create configuration surface.
 *
 * Rebalance (2026-06-19): the create form was overloaded with options that
 * belong AFTER creation (playback mode, timezone, output destination, ad
 * config). Those moved here, wired to the REAL `api.channels.updateChannel`
 * mutation (it `ctx.db.patch`es — the older "no real backend" note that
 * justified disabling these fields is stale). Bridge-buffer keeps its own
 * writer (`setBridgeBufferSeconds`). Slug stays read-only: it's the permanent
 * public-URL identity (`updateChannel` has no slug arg by design).
 *
 * Honest-state (CLAUDE.md §5): `useChannelList()` resolves the channel context
 * (Skeleton while loading, EmptyState if missing); `getChannelWithState` seeds
 * the full editable fields (timezone / loopEnabled / outputDestination /
 * description are not in the list projection). No fabricated values.
 * argKeys per V3I-INV-12: the wrapper logs keys only, never values.
 * ============================================================ */
import { useState } from 'react';
import { useQuery } from 'convex/react';
import { api } from '@convex/api';
import type { Id } from '@convex/dataModel';
import {
  Btn,
  EmptyState,
  Field,
  Input,
  PageHead,
  Panel,
  SegmentedControl,
  Select,
  Skeleton,
  Textarea,
} from '../../components/primitives';
import { navigate } from '../../lib/nav';
import { useMutationWithToast } from '../../hooks/useMutationWithToast';
import { useChannelList } from '../../hooks/useChannelList';

const SLUG_TOOLTIP = 'El slug se fija al crear el canal y no puede cambiarse (es la URL pública).';
const TIMEZONES = ['America/Mexico_City', 'America/Bogota', 'America/Buenos_Aires', 'Europe/Madrid'];

interface ChannelEditProps {
  id?: string;
}

export function ChannelEdit({ id }: ChannelEditProps) {
  const rows = useChannelList();
  const real = rows?.find((x) => x._id === id || x.slug === id);

  // Full channel doc for seeding the editable fields the list projection omits.
  const full = useQuery(
    api.channels.getChannelWithState,
    real ? { channelId: real._id as unknown as Id<'channels'> } : 'skip',
  ) as
    | {
        name?: string;
        description?: string;
        timezone?: string;
        loopEnabled?: boolean;
      }
    | null
    | undefined;

  const updateM = useMutationWithToast(api.channels.updateChannel, {
    logLabel: 'channel-edit-update',
    mutationName: 'channels.updateChannel',
    successMessage: 'Canal actualizado',
  });
  const { run: saveBuffer } = useMutationWithToast(
    api.mutations.channels.setBridgeBufferSeconds,
    {
      logLabel: 'channel-edit-buffer-save',
      mutationName: 'channels.setBridgeBufferSeconds',
      successMessage: 'Buffer actualizado',
    },
  );

  // Edits overlay the loaded values (form[k] ?? full[k]); `??` only falls
  // through on null/undefined, so an explicit `false` loopEnabled sticks.
  const [form, setForm] = useState<{
    name?: string;
    description?: string;
    timezone?: string;
    loopEnabled?: boolean;
  }>({});
  const [bufferSeconds, setBufferSeconds] = useState<number>(30);

  if (rows === undefined) {
    return <Skeleton h={420} />;
  }
  if (real === undefined) {
    return (
      <EmptyState
        icon="alertTriangle"
        title="Canal no encontrado"
        body="No se localizó el canal solicitado."
      />
    );
  }

  const channelId = real._id as unknown as Id<'channels'>;
  const vName = form.name ?? full?.name ?? real.name ?? '';
  const vDesc = form.description ?? full?.description ?? '';
  const vTz = form.timezone ?? full?.timezone ?? 'America/Mexico_City';
  const vLoop = form.loopEnabled ?? full?.loopEnabled ?? true;

  async function onSave(): Promise<void> {
    try {
      // Output destination + YouTube key moved to the dedicated "Destinos"
      // sub-tab (ChannelDestinations) — the single canonical destination
      // editor. General no longer writes those fields (NMW-233 / NMW-228).
      await updateM.run({
        channelId,
        name: vName.trim(),
        description: vDesc,
        timezone: vTz,
        loopEnabled: vLoop,
      });
      // Bridge buffer has its own writer; persist it alongside.
      await saveBuffer({ channelId, seconds: bufferSeconds });
      navigate('/channels/' + channelId);
    } catch {
      // The wrapper already surfaced the error toast; don't navigate on failure.
    }
  }

  return (
    <div>
      <PageHead
        crumbs={[
          { label: 'Canales', href: '#/channels' },
          { label: real.name, href: '#/channels/' + real._id },
          { label: 'Ajustes' },
        ]}
        title="Ajustes del canal"
      />
      <Panel pad={28} reviewed="wired">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <Field label="Nombre" required>
            <Input value={vName} onChange={(v: string) => setForm((f) => ({ ...f, name: v }))} reviewed="ok" />
          </Field>
          <Field label="Slug" hint={SLUG_TOOLTIP}>
            <span title={SLUG_TOOLTIP}>
              <Input value={real.slug ?? ''} onChange={() => undefined} prefix="circa.tv/v/" disabled reviewed="ok" />
            </span>
          </Field>
          <Field label="Descripción">
            <Textarea value={vDesc} onChange={(v: string) => setForm((f) => ({ ...f, description: v }))} reviewed="ok" />
          </Field>

          <div style={{ height: 1, background: 'var(--lightest-gray)' }} />

          <Field label="Modo de reproducción" hint="Bucle repite la programación; Lineal sigue las horas de inicio.">
            <SegmentedControl
              value={vLoop ? 'loop' : 'linear'}
              reviewed="ok"
              onChange={(v: string) => setForm((f) => ({ ...f, loopEnabled: v === 'loop' }))}
              options={[{ value: 'loop', label: 'Bucle' }, { value: 'linear', label: 'Lineal' }]}
            />
          </Field>
          <Field label="Zona horaria" hint="Usada para la programación lineal y la visualización.">
            <Select value={vTz} onChange={(v: string) => setForm((f) => ({ ...f, timezone: v }))} full reviewed="ok" options={TIMEZONES} />
          </Field>
          {/* Output destination + YouTube key live in the dedicated "Destinos"
              sub-tab (ChannelDestinations) — the single canonical editor. */}

          <div style={{ height: 1, background: 'var(--lightest-gray)' }} />

          <Field
            label="Segundos de buffer del puente (1-120)"
            hint="Duración del buffer del Bridge Worker antes de enviar al destino (solo YouTube/Ambos)."
          >
            <Input
              value={String(bufferSeconds)}
              onChange={(next: string) => {
                const parsed = Number.parseInt(next, 10);
                if (Number.isFinite(parsed)) setBufferSeconds(parsed);
              }}
              type="number"
              reviewed="ok"
            />
          </Field>

          <div>
            <Btn variant="solid" reviewed="wired" loading={updateM.loading} onClick={() => { void onSave(); }}>
              Guardar cambios
            </Btn>
          </div>
        </div>
      </Panel>
    </div>
  );
}

export default ChannelEdit;
